/**
 * Zod schemas mirroring the domain types in `types.ts`.
 *
 * These are the single validation boundary: every request body and every
 * supplier response field that touches the DB is parsed through one of these.
 */

import { z } from 'zod';
import {
  ACCESS_TYPES,
  AUTH_METHODS,
  FIELD_TRANSFORMS,
  LANGUAGES,
  ORDER_STATUSES,
  OUR_FIELDS,
  SUPPLIER_PRESETS,
  TRANSACTION_TYPES,
  WALLET_TX_STATUSES,
  WALLET_TX_TYPES,
} from './types.js';

// ─────────────────────────────────────────────────────────────
// Primitives
// ─────────────────────────────────────────────────────────────

export const objectIdSchema = z
  .string()
  .regex(/^[a-fA-F0-9]{24}$/, 'must be a 24-character hex ObjectId');

export const usdtSchema = z
  .number()
  .finite()
  .nonnegative()
  .refine((n) => Number.isFinite(n), 'must be a finite number');

export const slugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'must be a lowercase kebab-case slug');

export const accessTypeSchema = z.enum(ACCESS_TYPES);
export const authMethodSchema = z.enum(AUTH_METHODS);
export const fieldTransformSchema = z.enum(FIELD_TRANSFORMS);
export const orderStatusSchema = z.enum(ORDER_STATUSES);
export const ourFieldSchema = z.enum(OUR_FIELDS);
export const transactionTypeSchema = z.enum(TRANSACTION_TYPES);
export const languageSchema = z.enum(LANGUAGES);
export const walletTxTypeSchema = z.enum(WALLET_TX_TYPES);
export const walletTxStatusSchema = z.enum(WALLET_TX_STATUSES);

// ─────────────────────────────────────────────────────────────
// Supplier
// ─────────────────────────────────────────────────────────────

export const supplierFieldMappingSchema = z.object({
  ourField: ourFieldSchema,
  supplierPath: z.string().min(1).max(500),
  transform: fieldTransformSchema.optional(),
  multiplier: z.number().finite().min(0).max(1_000_000).optional(),
});

export const supplierPresetSchema = z.enum(SUPPLIER_PRESETS);

export const supplierApiConfigSchema = z.object({
  baseUrl: z.string().url().max(500),
  apiKey: z.string().min(1).max(2000),
  authMethod: authMethodSchema,
  preset: supplierPresetSchema.optional(),
  authHeaderName: z.string().min(1).max(100).optional(),
  authQueryParam: z.string().min(1).max(100).optional(),
  productsPath: z.string().max(500).optional(),
  productsArrayPath: z.string().max(500).optional(),
  createOrderPath: z.string().max(500).optional(),
  orderStatusPath: z.string().max(500).optional(),
  cancelOrderPath: z.string().max(500).optional(),
  balancePath: z.string().max(500).optional(),
  accountPath: z.string().max(500).optional(),
  balancePathField: z.string().max(500).optional(),
  ledgerPathField: z.string().max(500).optional(),
  orderProductParam: z.string().max(100).optional(),
  orderQuantityParam: z.string().max(100).optional(),
  orderProductIdNumeric: z.boolean().optional(),
  orderIdPathField: z.string().max(500).optional(),
  orderStatusPathField: z.string().max(500).optional(),
  deliveryDataPathField: z.string().max(500).optional(),
});

/** API-config patch: apiKey/baseUrl optional so the client can omit unchanged secrets. */
export const supplierApiConfigPatchSchema = supplierApiConfigSchema.partial();

export const createSupplierSchema = z.object({
  name: z.string().min(2).max(120),
  slug: slugSchema.optional(),
  isActive: z.boolean().default(true),
  walletAlertThreshold: usdtSchema.default(0),
  apiConfig: supplierApiConfigSchema,
  fieldMappings: z.array(supplierFieldMappingSchema).default([]),
  syncIntervalMinutes: z.number().int().min(1).max(1440).default(60),
});

export const updateSupplierSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  slug: slugSchema.optional(),
  isActive: z.boolean().optional(),
  walletAlertThreshold: usdtSchema.optional(),
  apiConfig: supplierApiConfigPatchSchema.optional(),
  fieldMappings: z.array(supplierFieldMappingSchema).optional(),
  syncIntervalMinutes: z.number().int().min(1).max(1440).optional(),
});

export const testConnectionSchema = z.object({
  /** Optional inline config to test before saving. */
  apiConfig: supplierApiConfigSchema.optional(),
});

// ─────────────────────────────────────────────────────────────
// Normalised product
// ─────────────────────────────────────────────────────────────

export const productSupplierEntrySchema = z.object({
  supplierId: objectIdSchema,
  externalProductId: z.string().min(1).max(200),
  costUsdt: usdtSchema,
  inStock: z.boolean(),
  stockCount: z.number().int().min(0).default(0),
  lastCheckedAt: z.string().datetime().optional(),
});

export const createProductSchema = z.object({
  name: z.string().min(2).max(200),
  slug: slugSchema.optional(),
  durationDays: z.number().int().min(0).max(36500).default(30),
  accessType: accessTypeSchema.default('unknown'),
  region: z
    .string()
    .min(2)
    .max(32)
    .transform((s) => s.toLowerCase())
    .default('global'),
  warrantyDays: z.number().int().min(0).max(3650).default(0),
  category: z.string().min(1).max(80).default('uncategorised'),
  tags: z.array(z.string().min(1).max(40)).max(30).default([]),
  description: z.string().max(4000).default(''),
  isActive: z.boolean().default(true),
  imageUrl: z.string().trim().max(500).refine((v) => v === '' || /^https?:\/\//i.test(v), 'must be a valid http(s) URL').default(''),
  pricingOverride: usdtSchema.nullable().optional(),
});

export const updateProductSchema = createProductSchema.partial();

export const setManualPriceSchema = z.object({
  pricingOverride: usdtSchema.nullable(),
});

// ─────────────────────────────────────────────────────────────
// Commission tiers
// ─────────────────────────────────────────────────────────────

export const commissionTierSchema = z
  .object({
    minCostUsdt: usdtSchema,
    maxCostUsdt: usdtSchema,
    fixedUsdt: usdtSchema.nullable().optional(),
    percentage: z.number().min(0).max(1000).nullable().optional(),
  })
  .refine((t) => t.maxCostUsdt >= t.minCostUsdt, {
    message: 'maxCostUsdt must be >= minCostUsdt',
    path: ['maxCostUsdt'],
  })
  .refine((t) => (t.fixedUsdt ?? 0) > 0 || (t.percentage ?? 0) > 0, {
    message: 'set a fixed fee, a percentage, or both',
    path: ['fixedUsdt'],
  });

export const updateCommissionTierSchema = z.object({
  minCostUsdt: usdtSchema.optional(),
  maxCostUsdt: usdtSchema.optional(),
  fixedUsdt: usdtSchema.nullable().optional(),
  percentage: z.number().min(0).max(1000).nullable().optional(),
});

// ─────────────────────────────────────────────────────────────
// Orders
// ─────────────────────────────────────────────────────────────

export const createOrderSchema = z.object({
  telegramUserId: z.number().int(),
  telegramUsername: z.string().max(64).nullable().optional(),
  productId: objectIdSchema,
  /** uuid v4; generated by the caller so retries are idempotent. */
  idempotencyKey: z.string().uuid(),
  quantity: z.number().int().min(1).max(10).default(1),
});

export const refundOrderSchema = z.object({
  note: z.string().max(500).optional(),
});

export const orderListQuerySchema = z.object({
  status: orderStatusSchema.optional(),
  supplierId: objectIdSchema.optional(),
  productId: objectIdSchema.optional(),
  search: z.string().max(120).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

// ─────────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  username: z.string().min(1).max(120),
  password: z.string().min(1).max(200),
});

// ─────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────

export const updateSettingsSchema = z.object({
  adminChatIds: z.array(z.number().int()).max(50).optional(),
  defaultWalletAlertThreshold: usdtSchema.optional(),
  webhookUrl: z.string().url().max(500).optional(),
  botToken: z.string().min(20).max(200).optional(),
  webAuthSecret: z.string().min(32).max(500).optional(),
  publicPriceMarkupPercent: z.number().min(0).max(1000).optional(),
  binanceApiKey: z.string().min(10).max(300).optional(),
  binanceApiSecret: z.string().min(10).max(300).optional(),
  binanceDepositAddress: z.string().min(8).max(300).optional(),
  binanceCoin: z.string().regex(/^[A-Z0-9]{2,20}$/).optional(),
  binanceNetwork: z.string().regex(/^[A-Z0-9_-]{2,30}$/).optional(),
  usdtTrc20Address: z.string().max(300).optional(),
  usdtBep20Address: z.string().max(300).optional(),
  binancePayId: z.string().max(100).optional(),
  jazzcashNumber: z.string().max(30).optional(),
  jazzcashAccountName: z.string().max(120).optional(),
  jazzcashPkrPerUsdt: z.number().positive().max(10000).optional(),
  /** Public @username (preferred) or numeric -100… chat id. Empty string clears the gate. */
  requiredGroupChatId: z.string().max(200).optional(),
  requiredGroupInviteLink: z.string().max(300).optional(),
  requiredChannelChatId: z.string().max(200).optional(),
  requiredChannelInviteLink: z.string().max(300).optional(),
});

// ─────────────────────────────────────────────────────────────
// Wallet (user balance top-ups)
// ─────────────────────────────────────────────────────────────

export const walletTxListQuerySchema = z.object({
  status: walletTxStatusSchema.optional(),
  telegramUserId: z.coerce.number().int().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

export const walletDecisionSchema = z.object({
  note: z.string().max(500).optional(),
});

export const walletAdjustSchema = z.object({
  telegramUserId: z.number().int(),
  amountUsdt: z.number().finite(),
  note: z.string().min(1).max(500),
});

export type WalletTxListQuery = z.infer<typeof walletTxListQuerySchema>;
export type WalletDecisionInput = z.infer<typeof walletDecisionSchema>;
export type WalletAdjustInput = z.infer<typeof walletAdjustSchema>;

// ─────────────────────────────────────────────────────────────
// Field-mapper preview
// ─────────────────────────────────────────────────────────────

export const previewMappingSchema = z.object({
  sample: z.unknown(),
  mappings: z.array(supplierFieldMappingSchema),
});

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
  search: z.string().max(120).optional(),
});

// ─────────────────────────────────────────────────────────────
// Inferred types
// ─────────────────────────────────────────────────────────────

export type CreateSupplierInput = z.infer<typeof createSupplierSchema>;
export type UpdateSupplierInput = z.infer<typeof updateSupplierSchema>;
export type CreateProductInput = z.infer<typeof createProductSchema>;
export type UpdateProductInput = z.infer<typeof updateProductSchema>;
export type CommissionTierInput = z.infer<typeof commissionTierSchema>;
export type UpdateCommissionTierInput = z.infer<typeof updateCommissionTierSchema>;
export type CreateOrderInput = z.infer<typeof createOrderSchema>;
export type OrderListQuery = z.infer<typeof orderListQuerySchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type UpdateSettingsInput = z.infer<typeof updateSettingsSchema>;
export type SupplierApiConfigInput = z.infer<typeof supplierApiConfigSchema>;
export type SupplierFieldMappingInput = z.infer<typeof supplierFieldMappingSchema>;
